import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { jobApi, onboardingApi } from '@/api/endpoints'
import { Button } from '@/components/ui/Button'
import { ErrorState, PageSpinner, Skeleton } from '@/components/ui/Feedback'
import { PageHeader } from '@/components/ui/Page'
import { StatusPill } from '@/components/ui/StatusPill'
import { Switch } from '@/components/ui/Toggle'
import { refreshLicense, useLicense } from '@/features/billing/useLicense'
import { isLicenseError, useLicenseStore } from '@/store/license'
import { toast } from '@/store/toast'
import type { ApplicationSettings, AutoApplyFilters, JobMatch, LicenseErrorCode } from '@/types/api'
import { AutoApplyScheduleCard, PowerUpsell, describeSchedule } from './AutoApplySchedule'
import { JobFilterBar } from './JobFilterBar'
import { ApplyBar, ConfirmApplyDialog, type ApplyIntent } from './autoApply/ApplyBar'
import { ActiveFilters, MatchScoreControl, SourceChip, useFilterChoices, useSourceOptions } from './autoApply/FilterControls'
import { CountLine, RefreshBanner, ResultsList, ResultsSkeleton } from './autoApply/Results'
import { SuggestionsCard } from './autoApply/SuggestionsCard'
import {
  DEFAULT_MIN_SCORE,
  applyResultText,
  applySuggestion,
  cleanFilters,
  filtersKey,
  formatNumber,
  fromBarValue,
  summarizeFilters,
  toBarValue,
} from './autoApply/filters'
import { invalidateAutoApplySearch, useAutoApplySearch } from './autoApply/useAutoApplySearch'

const SETTINGS_KEY = ['application-settings'] as const
const NO_SELECTION: ReadonlyMap<string, JobMatch> = new Map()
const LICENSE_CODES: LicenseErrorCode[] = ['subscription_required', 'subscription_inactive', 'quota_exhausted', 'feature_not_in_plan']

/**
 * Auto Apply: every opening that fits the profile, from the synced job
 * portals and the company career-page pool. Search and filter, see how many
 * are ready, apply to the filtered set (or a selection) in one go, widen the
 * search when it finds few, and save the filters for background Auto Apply.
 * The switch, the schedule and the Power gating sit in the right rail (first
 * on a phone).
 */
export function AutoApplyPage() {
  const settingsQuery = useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: onboardingApi.getSettings,
  })
  if (settingsQuery.isLoading || !settingsQuery.data) return <PageSpinner label="Loading auto apply" />
  return <AutoApplyBody settings={settingsQuery.data} />
}

function AutoApplyBody({ settings }: { settings: ApplicationSettings }) {
  const queryClient = useQueryClient()
  // Auto Apply, its schedule and the daily report come with Power.
  const license = useLicense()
  const autoLocked = Boolean(license.features && !license.features.autoApply)
  const canSchedule = Boolean(license.features?.scheduling)
  const defaultScore = settings.minMatchScore ?? DEFAULT_MIN_SCORE

  // The page opens on what background Auto Apply follows.
  const search = useAutoApplySearch(settings.autoApplyFilters ?? {}, true)
  const { options: sourceOptions, labels: sourceLabels } = useSourceOptions()
  const choices = useFilterChoices()
  const data = search.data

  // Ticked jobs, kept across pages; a new filter set starts empty.
  const [selection, setSelection] = useState(() => ({ key: search.committedKey, items: new Map<string, JobMatch>() }))
  const selected = selection.key === search.committedKey ? selection.items : NO_SELECTION
  const updateSelection = (change: (items: Map<string, JobMatch>) => void) => {
    const items = new Map(selected)
    change(items)
    setSelection({ key: search.committedKey, items })
  }

  const [countChoice, setCountChoice] = useState(50)
  const [intent, setIntent] = useState<ApplyIntent | null>(null)

  const remaining = data?.plan.remainingThisMonth ?? license.subscription?.usage?.remaining ?? null
  const remainingToday = data?.plan.remainingToday ?? null
  const ready = data?.totals.ready ?? 0
  const hasFilters = Object.keys(cleanFilters(search.filters)).length > 0

  // --- Switch and saved filters --------------------------------------------
  const saveSettings = (patch: Partial<ApplicationSettings>) => onboardingApi.saveSettings({ ...settings, ...patch })

  const toggle = useMutation({
    mutationFn: (enabled: boolean) => saveSettings({ autoApplyEnabled: enabled }),
    onSuccess: (updated) => {
      queryClient.setQueryData(SETTINGS_KEY, updated)
      toast.success(updated.autoApplyEnabled ? 'Auto apply is on' : 'Auto apply is off')
    },
    onError: (error: ApiError) => {
      if (!isLicenseError(error)) toast.error('Could not update', error.message)
    },
  })

  const saveFilters = useMutation({
    mutationFn: (filters: AutoApplyFilters | null) => saveSettings({ autoApplyFilters: filters }),
    onSuccess: (updated, filters) => {
      // An older server drops the field: keep what we sent so the summary is right.
      queryClient.setQueryData(SETTINGS_KEY, { ...updated, autoApplyFilters: updated.autoApplyFilters ?? filters })
      toast.success(
        filters ? 'Auto Apply will follow these filters' : 'Auto Apply follows every match again',
        filters ? summarizeFilters(filters, defaultScore, { choices, sourceLabels }) : undefined,
      )
    },
    onError: (error: ApiError) => {
      if (!isLicenseError(error)) toast.error('Could not save the filters', error.message)
    },
  })
  // Your own minimum, set or not, is the same filter set.
  const normalKey = (filters: AutoApplyFilters | null | undefined) =>
    filtersKey(filters && filters.minMatchScore === defaultScore ? { ...filters, minMatchScore: undefined } : filters)
  const savedKey = normalKey(settings.autoApplyFilters)
  const filtersSaved = savedKey === normalKey(search.committed)

  // --- Apply -----------------------------------------------------------------
  const openPlanPrompt = () => {
    const reason = data?.plan.reason
    useLicenseStore.getState().open({
      reason:
        reason && (LICENSE_CODES as string[]).includes(reason)
          ? (reason as LicenseErrorCode)
          : remaining === 0
            ? 'quota_exhausted'
            : 'subscription_required',
      plan: license.subscription?.subscription?.plan ?? null,
      remaining,
    })
  }
  const ask = (next: ApplyIntent) => {
    if ((data && !data.plan.canApply) || remaining === 0) return openPlanPrompt()
    license.requireApply(() => setIntent(next))
  }

  const apply = useMutation({
    mutationFn: (next: ApplyIntent) =>
      jobApi.autoApplyApply(
        next.kind === 'filters'
          ? { filters: search.committed, maxJobs: next.count }
          : { filters: search.committed, jobIds: next.jobs.map((match) => match.job.id) },
      ),
    onSuccess: (result) => {
      const queued = Array.isArray(result.queued) ? result.queued.length : result.queued
      const text = applyResultText(queued, result.skipped ?? [])
      toast.success(text.title, text.detail)
      setIntent(null)
      setSelection({ key: search.committedKey, items: new Map() })
      invalidateAutoApplySearch(queryClient)
      void queryClient.invalidateQueries({ queryKey: ['applications'] })
      void queryClient.invalidateQueries({ queryKey: ['application-stats'] })
      void queryClient.invalidateQueries({ queryKey: ['top-matches'] })
      refreshLicense(queryClient)
    },
    onError: (error: ApiError) => {
      setIntent(null)
      // A 402 already opened the upgrade dialog (api/client.ts).
      if (!isLicenseError(error)) toast.error('Could not apply', error.message)
    },
  })

  const planLine =
    remaining != null ? (
      <>
        Your plan has <span className="num font-medium text-ink-soft">{formatNumber(remaining)}</span> application
        {remaining === 1 ? '' : 's'} left this month.
      </>
    ) : license.subscription && !license.subscription.active ? (
      <>Choose a plan to start applying.</>
    ) : null

  return (
    <div>
      <PageHeader
        eyebrow="Application automation"
        title="Auto apply"
        description={
          <>
            Every opening that fits your profile, from your job portals and company career pages. {planLine}
          </>
        }
      />

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_320px] lg:grid-rows-[auto_1fr] lg:gap-5">
        <aside className="min-w-0 space-y-3 lg:col-start-2 lg:row-start-1">
          {autoLocked && <PowerUpsell onUpgrade={() => license.requireFeature('autoApply', () => undefined)} />}
          <ControlCard
            settings={settings}
            locked={autoLocked}
            dailyReport={Boolean(license.features?.dailyReport)}
            followsText={summarizeFilters(settings.autoApplyFilters, defaultScore, { choices, sourceLabels })}
            hasSavedFilters={Boolean(settings.autoApplyFilters && savedKey !== '{}')}
            onResetFilters={() => saveFilters.mutate(null)}
            resetting={saveFilters.isPending && saveFilters.variables === null}
            onToggle={(value) =>
              value ? license.requireFeature('autoApply', () => toggle.mutate(true)) : toggle.mutate(false)
            }
          />
        </aside>

        <div className="min-w-0 space-y-3 lg:col-start-1 lg:row-span-2 lg:row-start-1">
          <div className="space-y-2.5">
            <JobFilterBar
              value={toBarValue(search.filters)}
              onChange={(next) => search.setFilters(fromBarValue(next, search.filters))}
              searching={search.pending || (search.fetching && !search.isLoading)}
              hide={['exclude', 'searchMode', 'degreeLevel', 'easyApplyOnly', 'clear']}
              placeholder="Search by company or job title…"
              leading={<SourceChip filters={search.filters} options={sourceOptions} onChange={(next) => search.setFilters(next)} />}
            />
            <div className="rounded-field bg-surface-muted/60 px-3 py-2.5">
              <MatchScoreControl
                value={search.filters.minMatchScore}
                defaultValue={defaultScore}
                onChange={(score) => search.setFilters({ ...search.filters, minMatchScore: score })}
              />
            </div>
            <ActiveFilters
              filters={search.filters}
              choices={choices}
              sourceLabels={sourceLabels}
              defaultScore={defaultScore}
              onChange={(next) => search.setFilters(next, true)}
              onClear={() => search.setFilters({}, true)}
            />
          </div>

          {/* Count line and "use these filters". */}
          <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            {data ? (
              <CountLine totals={data.totals} sourceLabels={sourceLabels} />
            ) : search.isLoading ? (
              <Skeleton className="h-5 w-64" />
            ) : (
              <span />
            )}
            {data && (
              <Button
                size="sm"
                variant="secondary"
                className="shrink-0 self-start sm:self-auto"
                disabled={filtersSaved || search.pending}
                loading={saveFilters.isPending && saveFilters.variables !== null}
                icon={filtersSaved ? <CheckIcon /> : <FilterIcon className="h-3.5 w-3.5" />}
                onClick={() => license.requireFeature('autoApply', () => saveFilters.mutate(search.committed))}
                title={filtersSaved ? 'Background Auto Apply already follows these filters' : undefined}
              >
                {filtersSaved ? 'Auto Apply uses these filters' : 'Use these filters for Auto Apply'}
              </Button>
            )}
          </div>

          <RefreshBanner refreshing={search.refreshing} error={search.refreshing ? null : search.refreshError} />

          {search.isLoading ? (
            <ResultsSkeleton />
          ) : search.isError || !data ? (
            <div className="card">
              <ErrorState description="We could not load your matches." onRetry={search.retry} />
            </div>
          ) : ready === 0 ? (
            <SuggestionsCard
              ready={0}
              suggestions={data.suggestions ?? []}
              onPick={(suggestion) => search.setFilters(applySuggestion(search.committed, suggestion), true)}
              onClearAll={() => search.setFilters({}, true)}
              hasFilters={hasFilters}
            />
          ) : (
            <>
              <SuggestionsCard
                ready={ready}
                suggestions={data.suggestions ?? []}
                onPick={(suggestion) => search.setFilters(applySuggestion(search.committed, suggestion), true)}
                onClearAll={() => search.setFilters({}, true)}
                hasFilters={hasFilters}
              />
              <ResultsList
                items={data.items}
                pageInfo={data.pageInfo}
                selected={selected}
                dim={search.stale}
                sourceLabels={sourceLabels}
                onToggle={(match) =>
                  updateSelection((items) => {
                    if (items.has(match.job.id)) items.delete(match.job.id)
                    else items.set(match.job.id, match)
                  })
                }
                onSelectPage={(select) =>
                  updateSelection((items) => {
                    for (const match of data.items) {
                      if (select) items.set(match.job.id, match)
                      else items.delete(match.job.id)
                    }
                  })
                }
                onClearSelection={() => updateSelection((items) => items.clear())}
                onPage={(page) => {
                  search.setPage(page)
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }}
              />
              <ApplyBar
                ready={ready}
                remaining={remaining}
                choice={countChoice}
                onChoice={setCountChoice}
                selectedCount={selected.size}
                onClearSelection={() => updateSelection((items) => items.clear())}
                onApplyFiltered={(count) => ask({ kind: 'filters', count })}
                onApplySelected={() => ask({ kind: 'selected', jobs: [...selected.values()] })}
                busy={apply.isPending}
              />
            </>
          )}
        </div>

        {canSchedule && (
          <div className="min-w-0 lg:col-start-2 lg:row-start-2">
            <AutoApplyScheduleCard key={settings.updatedAt ?? 'unsaved'} settings={settings} />
          </div>
        )}
      </div>

      {intent && (
        <ConfirmApplyDialog
          intent={intent}
          filters={search.committed}
          choices={choices}
          sourceLabels={sourceLabels}
          defaultScore={defaultScore}
          remaining={remaining}
          remainingToday={remainingToday}
          busy={apply.isPending}
          onConfirm={() => apply.mutate(intent)}
          onClose={() => !apply.isPending && setIntent(null)}
        />
      )}
    </div>
  )
}

/** The switch, what it follows, and the two numbers it runs on. */
function ControlCard({
  settings,
  locked,
  dailyReport,
  followsText,
  hasSavedFilters,
  onResetFilters,
  resetting,
  onToggle,
}: {
  settings: ApplicationSettings
  /** The plan has no Auto Apply: the switch can only be turned off. */
  locked: boolean
  dailyReport: boolean
  followsText: string
  hasSavedFilters: boolean
  onResetFilters: () => void
  resetting: boolean
  onToggle: (enabled: boolean) => void
}) {
  const on = settings.autoApplyEnabled
  return (
    <section className="card overflow-hidden" aria-label="Auto apply controls">
      <div className="flex min-h-[56px] items-center justify-between gap-3 px-4 py-3 sm:px-5">
        <div className="flex min-w-0 items-center gap-2.5">
          <p className="text-[15px] font-semibold text-ink">Auto apply</p>
          {locked && !on ? (
            <StatusPill tone="neutral">Power plan</StatusPill>
          ) : (
            <StatusPill tone={on ? 'brand' : 'neutral'} live={on} title={on ? describeSchedule(settings.autoApplySchedule) : undefined}>
              {on ? (settings.autoApplySchedule ? 'On · scheduled' : 'On · daily') : 'Off'}
            </StatusPill>
          )}
        </div>
        <Switch checked={on} onChange={onToggle} label="Auto apply" disabled={locked && !on} />
      </div>

      <div className="flex items-start gap-2 border-t border-line px-4 py-2.5 text-[12.5px] sm:px-5">
        <FilterIcon />
        <p className="min-w-0 flex-1 leading-snug text-ink-muted">
          Auto Apply follows: <span className="font-medium text-ink">{followsText}</span>
          {!hasSavedFilters && <span className="block text-ink-faint">Save filters from the search to narrow it.</span>}
        </p>
        {hasSavedFilters && (
          <button
            type="button"
            onClick={onResetFilters}
            disabled={resetting}
            className="tap shrink-0 rounded-[6px] px-1 font-medium text-ink-soft hover:text-ink disabled:opacity-60"
          >
            Reset
          </button>
        )}
      </div>

      {dailyReport && (
        <p className="flex items-center gap-2 border-t border-line px-4 py-2.5 text-[12.5px] text-ink-muted sm:px-5">
          <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <rect x="1.75" y="3.25" width="12.5" height="9.5" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
            <path d="m2.5 4.5 5.5 4 5.5-4" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
          </svg>
          Daily report emailed each evening
        </p>
      )}

      <dl className="grid grid-cols-2 divide-x divide-line border-y border-line">
        <div className="px-4 py-3 sm:px-5">
          <dt className="text-[12px] text-ink-muted">Min match</dt>
          <dd className="num mt-0.5 text-[20px] font-semibold leading-none text-ink">{settings.minMatchScore}%</dd>
        </div>
        <div className="px-4 py-3 sm:px-5">
          <dt className="text-[12px] text-ink-muted">Daily limit</dt>
          <dd className="num mt-0.5 text-[20px] font-semibold leading-none text-ink">{settings.dailyApplicationLimit}</dd>
        </div>
      </dl>

      <div className="px-4 py-2 sm:px-5">
        <Link
          to="/dashboard/settings"
          className="inline-flex h-9 items-center gap-1.5 text-[13px] font-medium text-ink-soft transition-colors hover:text-ink"
        >
          Change rules in Settings
          <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="m4.5 2.5 3.5 3.5-3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Link>
      </div>
    </section>
  )
}

function FilterIcon({ className = 'mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-muted' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M2.5 3.5h11l-4.2 5v4l-2.6 1.2V8.5l-4.2-5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="m2.5 6.2 2.3 2.3 4.7-5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
